/* eslint-disable no-console */
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import axios from 'axios';

const PRINT_PAGE_AVOID_SELECTOR = '[class*="printMapBlock"]';

const getProtectedRanges = (container, canvas) => {
  const protectedElements = Array.from(container.querySelectorAll(PRINT_PAGE_AVOID_SELECTOR));
  const renderScale = canvas.height / container.scrollHeight;

  return protectedElements
    .map(element => ({
      start: element.offsetTop * renderScale,
      end: (element.offsetTop + element.offsetHeight) * renderScale
    }))
    .filter(range => range.end > range.start);
};

const adjustPageEnd = (pageStart, pageEnd, protectedRanges, pageHeightInCanvasPx) => {
  const overlappingRange = protectedRanges.find(
    range => range.start < pageEnd && range.end > pageEnd
  );

  if (!overlappingRange) {
    return pageEnd;
  }

  const protectedHeight = overlappingRange.end - overlappingRange.start;
  if (protectedHeight >= pageHeightInCanvasPx || overlappingRange.start <= pageStart) {
    return pageEnd;
  }

  return overlappingRange.start;
};

const fetchProfileLogo = async (profile) => {
  try {
    if (profile?.portal_airport?.logo) {
      const response = await fetch(profile.portal_airport.logo, {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: '0'
        },
        cache: 'no-cache'
      });
      if (response.status === 200) {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }
    }
  } catch (error) {
    console.error('Failed to fetch profile logo:', error);
  }
  return null;
};

const fetchAttachmentUrl = async (url) => {
  try {
    if (url.includes('attachments')) {
      const response = await axios.get(url, {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: '0'
        }
      });
      if (response.status === 200) {
        return URL.createObjectURL(response.data);
      }
    }
  } catch (error) {
    console.error('Failed to fetch attachment:', error);
  }
  return null;
};
const generatePDF = async (pdfName, profile) => {
  let clonedElement;
  try {
    const originalElement = document.getElementById('directDownload');
    if (!originalElement) return;
    clonedElement = originalElement.cloneNode(true);
    clonedElement.style.display = 'block';
    clonedElement.style.position = 'fixed';
    clonedElement.style.width = '800px';
    clonedElement.style.maxWidth = 'none';
    clonedElement.style.height = 'auto';
    clonedElement.style.overflow = 'hidden';
    clonedElement.style.left = '0';
    clonedElement.style.top = '0';
    clonedElement.style.opacity = '1';
    clonedElement.style.pointerEvents = 'none';
    clonedElement.style.zIndex = '-1';
    document.body.appendChild(clonedElement);
    const images = clonedElement.getElementsByTagName('img');
    await Promise.all(
      Array.from(images).map(async (img) => {
        const attachmentUrl = await fetchAttachmentUrl(img.src);
        if (attachmentUrl) {
          const imgElement = img;
          imgElement.src = attachmentUrl;
        }
      })
    );
    const logoUrl = await fetchProfileLogo(profile);
    Array.from(images).forEach((img) => {
      if (logoUrl && img.src === profile?.portal_airport?.logo) {
        const imgElement = img;
        imgElement.src = logoUrl;
      }
    });
    Array.from(clonedElement.querySelectorAll('.leaflet-container')).forEach((container) => {
      container.dispatchEvent(new Event('resize'));
    });
    await new Promise(resolve => setTimeout(resolve, 300));
    const canvas = await html2canvas(clonedElement, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    if (!imgData || !imgData.startsWith('data:image/png')) {
      throw new Error('Failed to generate valid PNG image data.');
    }

    // eslint-disable-next-line new-cap
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;

    const scaleFactor = 0.9;
    const scaledContentWidth = (pageWidth - margin * 2) * scaleFactor;
    const scaledContentHeightPerPage = (pageHeight - margin * 2) * scaleFactor;
    const xOffset = (pageWidth - scaledContentWidth) / 2;
    const scale = scaledContentWidth / canvas.width;
    const pageHeightInCanvasPx = scaledContentHeightPerPage / scale;
    const protectedRanges = getProtectedRanges(clonedElement, canvas);
    let sourceY = 0;
    let firstPage = true;

    while (sourceY < canvas.height) {
      let pageEnd = Math.min(sourceY + pageHeightInCanvasPx, canvas.height);
      pageEnd = adjustPageEnd(sourceY, pageEnd, protectedRanges, pageHeightInCanvasPx);
      const fragmentHeight = Math.max(1, pageEnd - sourceY);

      const canvasFragment = document.createElement('canvas');
      canvasFragment.width = canvas.width;
      canvasFragment.height = fragmentHeight;
      const fragmentContext = canvasFragment.getContext('2d');
      fragmentContext.drawImage(
        canvas,
        0,
        sourceY,
        canvas.width,
        fragmentHeight,
        0,
        0,
        canvas.width,
        fragmentHeight
      );

      const fragmentData = canvasFragment.toDataURL('image/png');
      if (!fragmentData || !fragmentData.startsWith('data:image/png')) {
        console.warn('Skipped corrupt fragment.');
        break;
      }
      if (!firstPage) {
        pdf.addPage();
      } else {
        firstPage = false;
      }
      pdf.addImage(
        fragmentData,
        'PNG',
        xOffset,
        margin,
        scaledContentWidth,
        fragmentHeight * scale
      );

      sourceY = pageEnd;
    }
    pdf.save(`${pdfName}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
  } finally {
    if (clonedElement?.parentNode) {
      clonedElement.parentNode.removeChild(clonedElement);
    }
  }
};

export default generatePDF;
